import React, { useState, useEffect } from 'react';
import { 
  Bell, Plus, Calendar, Clock, Send, Edit2, Trash2, CheckCircle2, 
  AlertCircle, RefreshCw, X, Users, ChevronRight, Eye, Play, Pause, AlertTriangle 
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios, { API_BASE_URL } from './apiConfig';
import moment from 'moment';

const SettingsShootNotices = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deliveriesModalOpen, setDeliveriesModalOpen] = useState(false);
  const [currentNotice, setCurrentNotice] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().add(30, 'days').format('YYYY-MM-DD'),
    booking_types: ['photoshoot'],
    trigger_type: 'after_booking',
    trigger_delay_minutes: 30,
    trigger_offset_hours: 24,
    status: 'active',
    resend_sent: false
  });

  // Preview recipients
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const token = localStorage.getItem('admin_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchNotices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/notices`, { headers });
      if (data.success) {
        setNotices(data.notices || []);
      }
    } catch (error) {
      console.error('Error fetching notices:', error);
      toast.error('Failed to load shoot notices');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentNotice(null);
    setFormData({
      title: '',
      message: 'Hi {name}, a quick heads-up before your shoot at Thaathayya Illu: our full-length standing mirror was damaged during a recent photoshoot and is currently unavailable. Its replacement is coming from Rajasthan and is expected to take 30–45 days.\n\nWe wanted to let you know in advance so you can plan your shots accordingly. Thank you for understanding!',
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().add(30, 'days').format('YYYY-MM-DD'),
      booking_types: ['photoshoot'],
      trigger_type: 'after_booking',
      trigger_delay_minutes: 30,
      trigger_offset_hours: 24,
      status: 'active',
      resend_sent: false
    });
    setModalOpen(true);
  };

  const openEditModal = (notice) => {
    setCurrentNotice(notice);
    setFormData({
      title: notice.title || '',
      message: notice.message || '',
      start_date: notice.start_date ? moment(notice.start_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      end_date: notice.end_date ? moment(notice.end_date).format('YYYY-MM-DD') : moment().add(30, 'days').format('YYYY-MM-DD'),
      booking_types: notice.booking_types || ['photoshoot'],
      trigger_type: notice.trigger_type || 'after_booking',
      trigger_delay_minutes: notice.trigger_delay_minutes || 30,
      trigger_offset_hours: notice.trigger_offset_hours || 24,
      status: notice.status || 'active',
      resend_sent: false
    });
    setModalOpen(true);
  };

  // Fetch recipient preview whenever dates, booking_types, or trigger settings change in modal
  useEffect(() => {
    if (!modalOpen) return;

    const timer = setTimeout(async () => {
      if (!formData.start_date || !formData.end_date) return;
      try {
        setPreviewLoading(true);
        const { data } = await axios.post(
          `${API_BASE_URL}/api/admin/notices/preview-recipients`,
          {
            start_date: formData.start_date,
            end_date: formData.end_date,
            booking_types: formData.booking_types,
            trigger_type: formData.trigger_type,
            trigger_delay_minutes: formData.trigger_delay_minutes,
            trigger_offset_hours: formData.trigger_offset_hours
          },
          { headers }
        );
        if (data.success) {
          setPreviewRecipients(data.recipients || []);
        }
      } catch (err) {
        console.error('Preview error:', err);
      } finally {
        setPreviewLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [formData.start_date, formData.end_date, formData.booking_types, formData.trigger_type, formData.trigger_delay_minutes, formData.trigger_offset_hours, modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveNotice = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Notice title is required');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Notice message is required');
      return;
    }

    try {
      setSaving(true);
      if (currentNotice) {
        // Edit existing notice
        const { data } = await axios.put(
          `${API_BASE_URL}/api/admin/notices/${currentNotice.id}`,
          formData,
          { headers }
        );
        if (data.success) {
          toast.success('Notice updated successfully');
          setModalOpen(false);
          fetchNotices();
        }
      } else {
        // Create new notice
        const { data } = await axios.post(
          `${API_BASE_URL}/api/admin/notices`,
          formData,
          { headers }
        );
        if (data.success) {
          toast.success(`Notice created! Queued for ${data.queueResult?.queuedCount || 0} booking(s).`);
          setModalOpen(false);
          fetchNotices();
        }
      }
    } catch (error) {
      console.error('Error saving notice:', error);
      toast.error(error.response?.data?.message || 'Failed to save notice');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (notice) => {
    const newStatus = notice.status === 'active' ? 'paused' : 'active';
    try {
      const { data } = await axios.put(
        `${API_BASE_URL}/api/admin/notices/${notice.id}`,
        { status: newStatus },
        { headers }
      );
      if (data.success) {
        toast.success(`Notice marked as ${newStatus}`);
        fetchNotices();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteNotice = async (notice) => {
    if (!window.confirm(`Delete notice "${notice.title}"? Any pending queued messages will be cancelled.`)) {
      return;
    }
    try {
      const { data } = await axios.delete(`${API_BASE_URL}/api/admin/notices/${notice.id}`, { headers });
      if (data.success) {
        toast.success('Notice deleted');
        fetchNotices();
      }
    } catch (error) {
      toast.error('Failed to delete notice');
    }
  };

  const openDeliveriesModal = async (notice) => {
    setCurrentNotice(notice);
    setDeliveriesModalOpen(true);
    try {
      setLoadingDeliveries(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/notices/${notice.id}/deliveries`, { headers });
      if (data.success) {
        setDeliveries(data.deliveries || []);
      }
    } catch (error) {
      toast.error('Failed to load deliveries');
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const handleResendSingle = async (noticeId, deliveryId) => {
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/admin/notices/${noticeId}/deliveries/${deliveryId}/resend`,
        {},
        { headers }
      );
      if (data.success) {
        toast.success('Message scheduled for immediate re-send');
        // Refresh deliveries list
        const res = await axios.get(`${API_BASE_URL}/api/admin/notices/${noticeId}/deliveries`, { headers });
        if (res.data.success) setDeliveries(res.data.deliveries || []);
        fetchNotices();
      }
    } catch (error) {
      toast.error('Failed to trigger resend');
    }
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      message: prev.message + ` ${variable}`
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-50 bg-gray-50/30">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#471F21]/5 text-[#471F21] rounded-xl">
              <Bell className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Shoot Notices & Broadcasts</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Send automated WhatsApp notices for prop updates, shoot guidelines, or announcements across a date range.
              </p>
            </div>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#471F21] text-white font-semibold rounded-xl hover:bg-[#351719] transition-all shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Create Notice
          </button>
        </div>

        {/* Notices List */}
        <div className="p-6 sm:p-8">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin mb-3 text-[#471F21]" />
              <p className="text-sm font-medium">Loading shoot notices...</p>
            </div>
          ) : notices.length === 0 ? (
            <div className="py-16 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
              <div className="w-12 h-12 rounded-full bg-[#471F21]/5 text-[#471F21] flex items-center justify-center mx-auto mb-3">
                <Bell className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-gray-800 mb-1">No Active Notices</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
                Create a notice when a prop is damaged or when you have an important announcement for all upcoming shoots in a date range.
              </p>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#471F21] text-white text-sm font-semibold rounded-xl hover:bg-[#351719]"
              >
                <Plus className="w-4 h-4" />
                Create First Notice
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {notices.map(notice => (
                <div
                  key={notice.id}
                  className="p-5 rounded-2xl border border-gray-100 hover:border-gray-200 bg-white hover:shadow-sm transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${
                        notice.status === 'active' 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }`}>
                        {notice.status === 'active' ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            Active
                          </>
                        ) : (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                            Paused
                          </>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{notice.title}</h3>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            {moment(notice.start_date).format('DD MMM YYYY')} – {moment(notice.end_date).format('DD MMM YYYY')}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1 font-medium text-gray-600">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            {notice.trigger_type === 'immediate' && 'Immediate Send'}
                            {notice.trigger_type === 'after_booking' && `${notice.trigger_delay_minutes || 30} mins after confirmation`}
                            {notice.trigger_type === 'before_shoot' && `${notice.trigger_offset_hours || 24} hours before shoot`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openDeliveriesModal(notice)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                        title="View Queue and Delivery Audit"
                      >
                        <Eye className="w-3.5 h-3.5 text-gray-500" />
                        Queue & Log
                      </button>
                      <button
                        onClick={() => handleToggleStatus(notice)}
                        className={`p-1.5 rounded-lg border text-xs font-medium transition-colors ${
                          notice.status === 'active' 
                            ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100' 
                            : 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                        }`}
                        title={notice.status === 'active' ? 'Pause Notice' : 'Resume Notice'}
                      >
                        {notice.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => openEditModal(notice)}
                        className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                        title="Edit Notice"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteNotice(notice)}
                        className="p-1.5 rounded-lg border border-red-100 text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete Notice"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Message snippet */}
                  <div className="p-3.5 bg-gray-50/70 rounded-xl border border-gray-100 text-sm text-gray-700 whitespace-pre-line leading-relaxed mb-4">
                    {notice.message}
                  </div>

                  {/* Delivery stats bar */}
                  <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-50 text-xs">
                    <span className="text-gray-400 font-medium">Delivery Stats:</span>
                    <span className="flex items-center gap-1.5 font-bold text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      {notice.sent_count || 0} Sent
                    </span>
                    <span className="flex items-center gap-1.5 font-bold text-blue-700">
                      <Clock className="w-3.5 h-3.5 text-blue-500" />
                      {notice.pending_count || 0} Pending
                    </span>
                    {parseInt(notice.failed_count, 10) > 0 && (
                      <span className="flex items-center gap-1.5 font-bold text-red-600">
                        <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                        {notice.failed_count} Failed
                      </span>
                    )}
                    <span className="text-gray-400 ml-auto">
                      Total: {notice.total_deliveries || 0} recipient(s)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── CREATE / EDIT MODAL ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur-sm z-10">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {currentNotice ? 'Edit Shoot Notice' : 'Create Shoot Notice'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure the message, affected dates, and delivery timing.
                </p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveNotice} className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Notice Title (Internal)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Standing Mirror Unavailable"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:bg-white focus:border-[#471F21] outline-none transition-all"
                  required
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Affected Dates: Start
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:bg-white focus:border-[#471F21] outline-none transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Affected Dates: End
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    min={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:bg-white focus:border-[#471F21] outline-none transition-all"
                    required
                  />
                </div>
              </div>

              {/* Timing Trigger */}
              <div className="p-4 bg-[#F4ECDE]/30 rounded-xl border border-[#471F21]/10 space-y-3">
                <label className="block text-xs font-bold text-[#471F21] uppercase tracking-wider">
                  When should this WhatsApp notice be sent?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, trigger_type: 'after_booking' })}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      formData.trigger_type === 'after_booking'
                        ? 'border-[#471F21] bg-white shadow-sm ring-1 ring-[#471F21]'
                        : 'border-gray-200 bg-white/50 hover:bg-white'
                    }`}
                  >
                    <p className="text-xs font-bold text-gray-900 mb-0.5">After Booking</p>
                    <p className="text-[11px] text-gray-500">Wait X minutes after booking confirmation</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, trigger_type: 'before_shoot' })}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      formData.trigger_type === 'before_shoot'
                        ? 'border-[#471F21] bg-white shadow-sm ring-1 ring-[#471F21]'
                        : 'border-gray-200 bg-white/50 hover:bg-white'
                    }`}
                  >
                    <p className="text-xs font-bold text-gray-900 mb-0.5">Before Shoot</p>
                    <p className="text-[11px] text-gray-500">Send N hours/days before scheduled slot</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, trigger_type: 'immediate' })}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      formData.trigger_type === 'immediate'
                        ? 'border-[#471F21] bg-white shadow-sm ring-1 ring-[#471F21]'
                        : 'border-gray-200 bg-white/50 hover:bg-white'
                    }`}
                  >
                    <p className="text-xs font-bold text-gray-900 mb-0.5">Immediately</p>
                    <p className="text-[11px] text-gray-500">Dispatch right away to all eligible</p>
                  </button>
                </div>

                {/* Sub-options based on trigger */}
                {formData.trigger_type === 'after_booking' && (
                  <div className="pt-2 flex items-center gap-3">
                    <span className="text-xs text-gray-600 font-medium">Send delay after confirmation:</span>
                    <select
                      value={formData.trigger_delay_minutes}
                      onChange={(e) => setFormData({ ...formData, trigger_delay_minutes: Number(e.target.value) })}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-800 outline-none focus:border-[#471F21]"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour (60 mins)</option>
                      <option value={120}>2 hours (120 mins)</option>
                    </select>
                  </div>
                )}

                {formData.trigger_type === 'before_shoot' && (
                  <div className="pt-2 flex items-center gap-3">
                    <span className="text-xs text-gray-600 font-medium">Send how long before shoot begins:</span>
                    <select
                      value={formData.trigger_offset_hours}
                      onChange={(e) => setFormData({ ...formData, trigger_offset_hours: Number(e.target.value) })}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-800 outline-none focus:border-[#471F21]"
                    >
                      <option value={12}>12 hours before</option>
                      <option value={24}>24 hours (1 day before)</option>
                      <option value={48}>48 hours (2 days before)</option>
                      <option value={72}>72 hours (3 days before)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Message */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                    WhatsApp Message Text
                  </label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-gray-400 font-medium">Insert tag:</span>
                    {['{name}', '{date}', '{time}'].map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => insertVariable(tag)}
                        className="px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-[11px] font-mono transition-colors"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Type your WhatsApp notification message here..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-normal focus:bg-white focus:border-[#471F21] outline-none transition-all leading-relaxed"
                  required
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Variables like <code className="font-mono">{'{name}'}</code> will be automatically replaced with the guest's name.
                </p>
              </div>

              {/* Editing existing notice: Resend option */}
              {currentNotice && (
                <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="resend_sent"
                    checked={formData.resend_sent}
                    onChange={(e) => setFormData({ ...formData, resend_sent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-[#471F21] rounded border-gray-300 focus:ring-0"
                  />
                  <label htmlFor="resend_sent" className="text-xs text-amber-900 leading-normal cursor-pointer">
                    <span className="font-bold">Resend updated message to guests who already received the previous version</span>
                    <span className="block text-amber-700 mt-0.5">
                      If unchecked, only pending scheduled sends and future fresh bookings will receive the updated message.
                    </span>
                  </label>
                </div>
              )}

              {/* Recipient preview */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200/70">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-gray-500" />
                    Recipient Preview: {previewRecipients.length} matching booking(s) in date range
                  </span>
                  {previewLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                </div>

                {previewRecipients.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No confirmed bookings found in this date range.</p>
                ) : (
                  <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                    {previewRecipients.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded-lg border border-gray-100">
                        <div className="font-semibold text-gray-800">
                          {r.user_name} <span className="font-normal text-gray-400">({r.phone})</span>
                        </div>
                        <div className="text-right text-[11px] text-gray-500">
                          Shoot: {r.shoot_date} · Send: <span className="font-medium text-emerald-700">{moment(r.scheduled_for).format('DD MMM, hh:mm A')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#471F21] text-white text-sm font-bold rounded-xl hover:bg-[#351719] transition-all shadow-sm disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {currentNotice ? 'Update Notice' : 'Save & Queue Notice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELIVERIES & QUEUE MODAL ── */}
      {deliveriesModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur-sm z-10">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Delivery Queue & Log: {currentNotice?.title}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Audit of all guests scheduled or sent this WhatsApp notice.
                </p>
              </div>
              <button
                onClick={() => setDeliveriesModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {loadingDeliveries ? (
                <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                  <RefreshCw className="w-6 h-6 animate-spin mb-2 text-[#471F21]" />
                  <p className="text-xs">Loading deliveries...</p>
                </div>
              ) : deliveries.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">
                  No delivery records found for this notice yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-100 text-gray-400 uppercase tracking-wider text-[10px]">
                        <th className="pb-3 font-bold">Guest</th>
                        <th className="pb-3 font-bold">Shoot Date</th>
                        <th className="pb-3 font-bold">Scheduled For</th>
                        <th className="pb-3 font-bold">Status</th>
                        <th className="pb-3 font-bold text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {deliveries.map(d => (
                        <tr key={d.id} className="hover:bg-gray-50/50">
                          <td className="py-3">
                            <p className="font-bold text-gray-800">{d.recipient_name || d.booking_user_name || 'Guest'}</p>
                            <p className="text-[11px] text-gray-400">{d.recipient_phone}</p>
                          </td>
                          <td className="py-3 text-gray-600 font-medium">
                            {d.slot_time ? moment(d.slot_time).format('DD MMM YYYY') : '—'}
                          </td>
                          <td className="py-3">
                            <p className="font-medium text-gray-700">{moment(d.scheduled_for).format('DD MMM, hh:mm A')}</p>
                            {d.sent_at && (
                              <p className="text-[10px] text-emerald-600">Sent: {moment(d.sent_at).format('hh:mm A')}</p>
                            )}
                          </td>
                          <td className="py-3">
                            {d.status === 'sent' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700">
                                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                Sent
                              </span>
                            )}
                            {d.status === 'pending' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700">
                                <Clock className="w-3 h-3 text-blue-500" />
                                Pending
                              </span>
                            )}
                            {d.status === 'failed' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700" title={d.error_message}>
                                <AlertTriangle className="w-3 h-3 text-red-500" />
                                Failed
                              </span>
                            )}
                          </td>
                          <td className="py-3 text-right">
                            <button
                              onClick={() => handleResendSingle(d.notice_id, d.id)}
                              className="px-2.5 py-1 text-[11px] font-bold text-[#471F21] hover:bg-[#471F21]/10 rounded-lg transition-colors border border-[#471F21]/20"
                            >
                              Resend Now
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsShootNotices;
